// src/pages/auths/AdminRegistration.tsx
import React, {useState, useEffect } from 'react';
import {useNavigate } from 'react-router-dom';
import {
  createUserWithEmailAndPassword, 
  updateProfile, 
  deleteUser,
  updateEmail,
  updatePassword // Imported for client-side password change
} from 'firebase/auth';
import {doc, setDoc, getDocs, collection, updateDoc, deleteDoc } from 'firebase/firestore';
import {auth, db } from '../../firebase';
import styled from 'styled-components';

const Container = styled.div`
  display: flex;
  justify-content: center;
  min-height: 80vh;
  background-color: #f5f5f5;
  font-family: Arial, sans-serif;
  padding: 20px;
`;

const ContentWrapper = styled.div`
  display: flex;
  gap: 30px;
  width: 100%;
  max-width: 1200px;
`;

const FormSection = styled.div`
  flex: 1;
  background-color: white;
  padding: 2rem;
  border-radius: 8px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  max-width: 450px;
`;

const ListSection = styled.div`
  flex: 2;
  background-color: white;
  padding: 2rem;
  border-radius: 8px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
`;

const Title = styled.h2`
  text-align: center;
  margin-bottom: 1.5rem;
  color: #333;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
`;

const InputGroup = styled.div`
  margin-bottom: 1rem;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  font-weight: bold;
  color: #555;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 1rem;
  box-sizing: border-box;
`;

const Select = styled.select`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 1rem;
  box-sizing: border-box;
`;

const Button = styled.button<{$loading?: boolean; $variant?: 'primary' | 'secondary' | 'danger' }>`
  padding: 0.75rem;
  background-color: ${props => {
    if (props.$loading) return '#aaa';
    if (props.$variant === 'danger') return '#e74c3c';
    if (props.$variant === 'secondary') return '#95a5a6';
    return '#2c3e50';
  }};
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 1rem;
  cursor: ${props => props.$loading ? 'not-allowed' : 'pointer'};
  transition: background-color 0.3s;
  margin-top: 0.5rem;
  margin-right: 0.5rem;
  
  &:hover {
    background-color: ${props => {
      if (props.$loading) return '#aaa';
      if (props.$variant === 'danger') return '#c0392b';
      if (props.$variant === 'secondary') return '#7f8c8d';
      return '#34495e';
    }};
  }
`;

const Error = styled.div`
  color: #d32f2f;
  background-color: #ffebee;
  padding: 0.75rem;
  border-radius: 4px;
  margin-bottom: 1rem;
  text-align: center;
`;

const Success = styled.div`
  color: #2e7d32;
  background-color: #edf7ed;
  padding: 0.75rem;
  border-radius: 4px;
  margin-bottom: 1rem;
  text-align: center;
`;

const AdminTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-top: 1rem;
`;

const TableHeader = styled.th`
  background-color: #f8f9fa;
  padding: 12px;
  text-align: left;
  border-bottom: 2px solid #ddd;
  font-weight: bold;
`;

const TableCell = styled.td`
  padding: 12px;
  border-bottom: 1px solid #ddd;
`;

const TableRow = styled.tr`
  &:hover {
    background-color: #f8f9fa;
  }
`;

const ActionButton = styled.button<{$variant?: 'edit' | 'delete' }>`
  padding: 0.5rem 1rem;
  background-color: ${props => props.$variant === 'delete' ? '#e74c3c' : '#3498db'};
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  margin-right: 0.5rem;
  
  &:hover {
    background-color: ${props => props.$variant === 'delete' ? '#c0392b' : '#2980b9'};
  }
`;

// --- UPDATED INTERFACE with username ---
interface Admin {
  uid: string;
  email: string;
  firstName: string;
  lastName: string;
  username: string; // ADDED
  role: string;
  createdAt: Date;
  isActive: boolean;
}

const AdminRegistration: React.FC = () => {
  const [formData, setFormData] = useState({
    uid: '',
    firstName: '',
    lastName: '',
    username: '', // ADDED
    email: '',
    password: '',
    confirmPassword: '',
    role: 'admin',
    isActive: true
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [admins, setAdmins] = useState<Admin[]>([]);
  const navigate = useNavigate();

  useEffect(() => {
    fetchAdmins();
  }, []);

  const fetchAdmins = async () => {
    try {
      const querySnapshot = await getDocs(collection(db, 'admins'));
      const adminsData: Admin[] = [];
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        adminsData.push({
          uid: doc.id, 
          ...data, 
          createdAt: data.createdAt?.toDate() || new Date(),
        } as Admin);
      });
      setAdmins(adminsData);
    } catch (error) {
      console.error('Error fetching admins:', error);
      setError('Failed to fetch admins');
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    if (error) setError('');
    if (success) setSuccess('');
  };

  const handleEdit = (admin: Admin) => {
    setFormData({
      uid: admin.uid,
      firstName: admin.firstName,
      lastName: admin.lastName,
      username: admin.username, // ADDED
      email: admin.email,
      password: '',
      confirmPassword: '',
      role: admin.role,
      isActive: admin.isActive
    });
    setIsEditing(true);
    setError('');
    setSuccess('');
  };

  const handleCancelEdit = () => {
    setFormData({
      uid: '',
      firstName: '',
      lastName: '',
      username: '', // ADDED
      email: '',
      password: '',
      confirmPassword: '',
      role: 'admin',
      isActive: true
    });
    setIsEditing(false);
    setError('');
    setSuccess('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    setSuccess('');

    if (isEditing) {
      await updateAdmin();
    } else {
      await createAdmin();
    }
  };

  const createAdmin = async () => {
    // Validation
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      setIsLoading(false);
      return;
    }

    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters long');
      setIsLoading(false);
      return;
    }

    try {
      // Create user in Firebase Auth
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        formData.email,
        formData.password
      );

      const user = userCredential.user;

      // Update user profile with name and potentially username (if Auth allows)
      await updateProfile(user, {
        displayName: `${formData.firstName} ${formData.lastName}`
      });

      // Create admin document in Firestore
      const adminData = {
        uid: user.uid,
        email: formData.email,
        firstName: formData.firstName,
        lastName: formData.lastName,
        username: formData.username, // ADDED
        role: formData.role,
        createdAt: new Date(),
        isActive: formData.isActive
      };

      await setDoc(doc(db, 'admins', user.uid), adminData);

      setSuccess('Admin created successfully!');
      handleCancelEdit();
      fetchAdmins();

    } catch (error: any) {
      console.error('Error creating admin:', error);
      setError(error.message || 'Failed to create admin');
    } finally {
      setIsLoading(false);
    }
  };

  const updateAdmin = async () => {
    try {
      const adminData: any = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        username: formData.username, // ADDED
        email: formData.email,
        role: formData.role,
        isActive: formData.isActive
      };

      // If password is provided, update it
      if (formData.password) {
        if (formData.password !== formData.confirmPassword) {
          setError('Passwords do not match');
          setIsLoading(false);
          return;
        }

        if (formData.password.length < 6) {
          setError('Password must be at least 6 characters long');
          setIsLoading(false);
          return;
        }

        const user = auth.currentUser;
        // The check below is a necessary security constraint for client-side Auth updates.
        // **IMPORTANT FIX:** The line below was commented out. It is now UNCOMMENTED.
        // NOTE: This client-side call will only work reliably if the admin BEING EDITED 
        // is the SAME user who is currently logged in AND they have recently signed in.
        // For a true admin panel, this should be done via a Firebase Cloud Function (Admin SDK).
        if (user && user.uid === formData.uid) {
           await updatePassword(user, formData.password); 
        } else {
           // Optionally add a warning here if the user is not the current user
           console.warn("Client-side password update bypassed for non-current user. Use Admin SDK!");
        }
      }

      // Update Firestore document
      await updateDoc(doc(db, 'admins', formData.uid), adminData);

      setSuccess('Admin updated successfully!');
      handleCancelEdit();
      fetchAdmins();

    } catch (error: any) {
      console.error('Error updating admin:', error);
      setError(error.message || 'Failed to update admin');
    } finally {
      setIsLoading(false);
    }
  };

// src/pages/auths/AdminRegistration.tsx

const handleDelete = async (admin: Admin) => {
    if (!window.confirm(`Are you sure you want to delete ${admin.firstName} ${admin.lastName}? This action cannot be undone.`)) {
      return;
    }
    
    setIsLoading(true);
    setError('');
    setSuccess('');

    try {
      // 1. Delete from Firestore
      await deleteDoc(doc(db, 'admins', admin.uid));
      
      // 2. Delete the user from Firebase Auth
      
      const userToDelete = auth.currentUser;
      
      // Client-side deletion only works if the user being deleted is the currently logged-in user.
      // If the currently logged-in Admin is trying to delete *their own account*, this will work.
      if (userToDelete && userToDelete.uid === admin.uid) {
        // 🔑 FIX: This line MUST be called to remove the email from Firebase Auth.
        // NOTE: This will also log the current user out immediately.
        await deleteUser(userToDelete); 
        console.warn("Current user's Auth account deleted successfully.");
      } else {
        console.warn("Cannot delete another user's Auth account from the client. Auth entry persists.");
      }

      setSuccess('Admin deleted successfully!');
      setAdmins(prev => prev.filter(a => a.uid !== admin.uid));
      
    } catch (error: any) {
      console.error('Error deleting admin:', error);
      setError(error.message || 'Failed to delete admin. The Auth account may still exist.');
    } finally {
      setIsLoading(false);
    }
  };

  const toggleAdminStatus = async (admin: Admin) => {
    try {
      await updateDoc(doc(db, 'admins', admin.uid), {
        isActive: !admin.isActive
      });
      setSuccess(`Admin ${!admin.isActive ? 'activated' : 'deactivated'} successfully!`);
      fetchAdmins(); 
    } catch (error: any) {
      console.error('Error toggling admin status:', error);
      setError(error.message || 'Failed to update admin status');
    }
  };

  return (
    <Container>
      <ContentWrapper>
        <FormSection>
          <Title>{isEditing ? 'Edit Admin' : 'Register New Admin'}</Title>
          <Form onSubmit={handleSubmit}>
            <InputGroup>
              <Label htmlFor="firstName">First Name</Label>
              <Input
                type="text"
                id="firstName"
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                placeholder="Enter first name"
                required
              />
            </InputGroup>

            <InputGroup>
              <Label htmlFor="lastName">Last Name</Label>
              <Input
                type="text"
                id="lastName"
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
                placeholder="Enter last name"
                required
              />
            </InputGroup>
            
            {/* --- ADDED USERNAME FIELD --- */}
            <InputGroup>
              <Label htmlFor="username">Username</Label>
              <Input
                type="text"
                id="username"
                name="username"
                value={formData.username}
                onChange={handleChange}
                placeholder="Enter unique username"
                required
              />
            </InputGroup>
            {/* --------------------------- */}

            <InputGroup>
              <Label htmlFor="email">Email</Label>
              <Input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="Enter email address"
                required
              />
            </InputGroup>

            <InputGroup>
              <Label htmlFor="role">Role</Label>
              <Select
                id="role"
                name="role"
                value={formData.role}
                onChange={handleChange}
                required
              >
                <option value="admin">Admin</option>
                <option value="superadmin">Super Admin</option>
                <option value="moderator">Moderator</option>
              </Select>
            </InputGroup>

            <InputGroup>
              <Label htmlFor="password">
                {isEditing ? 'New Password (leave blank to keep current)' : 'Password'}
              </Label>
              <Input
                type="password"
                id="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                placeholder={isEditing ? "Enter new password" : "Enter password (min 6 characters)"}
                required={!isEditing}
              />
            </InputGroup>

            <InputGroup>
              <Label htmlFor="confirmPassword">Confirm Password</Label>
              <Input
                type="password"
                id="confirmPassword"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                placeholder="Confirm password"
                required={!isEditing}
              />
            </InputGroup>

            {isEditing && (
              <InputGroup>
                <Label htmlFor="isActive">Status</Label>
                <Select
                  id="isActive"
                  name="isActive"
                  value={formData.isActive.toString()}
                  onChange={(e) => setFormData({...formData, isActive: e.target.value === 'true'})}
                >
                  <option value="true">Active</option>
                  <option value="false">Inactive</option>
                </Select>
              </InputGroup>
            )}

            {error && <Error>{error}</Error>}
            {success && <Success>{success}</Success>}

            <div>
              <Button 
                type="submit" 
                $loading={isLoading}
                disabled={isLoading}
              >
                {isLoading ? 'Processing...' : isEditing ? 'Update Admin' : 'Create Admin'}
              </Button>
              
              {isEditing && (
                <Button 
                  type="button" 
                  $variant="secondary"
                  onClick={handleCancelEdit}
                  disabled={isLoading}
                >
                  Cancel
                </Button>
              )}
            </div>
          </Form>
        </FormSection>

        <ListSection>
          <Title>Admin Users</Title>
          {admins.length === 0 ? (
            <p>No admin users found.</p>
          ) : (
            <AdminTable>
              <thead>
                <tr>
                  <TableHeader>Name</TableHeader>
                  <TableHeader>Username</TableHeader> {/* ADDED */}
                  <TableHeader>Email</TableHeader>
                  <TableHeader>Role</TableHeader>
                  <TableHeader>Status</TableHeader>
                  <TableHeader>Actions</TableHeader>
                </tr>
              </thead>
              <tbody>
                {admins.map((admin) => (
                  <TableRow key={admin.uid}>
                    <TableCell>{admin.firstName} {admin.lastName}</TableCell>
                    <TableCell>{admin.username}</TableCell> {/* ADDED */}
                    <TableCell>{admin.email}</TableCell>
                    <TableCell>{admin.role}</TableCell>
                    <TableCell>
                      <span style={{
                        color: admin.isActive ? '#27ae60' : '#e74c3c',
                        fontWeight: 'bold'
                      }}>
                        {admin.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </TableCell>
                    <TableCell>
                      <ActionButton 
                        $variant="edit" 
                        onClick={() => handleEdit(admin)}
                      >
                        Edit
                      </ActionButton>
                      <ActionButton 
                        $variant="delete" 
                        onClick={() => handleDelete(admin)}
                      >
                        Delete
                      </ActionButton>
                      <Button 
                        $variant={admin.isActive ? 'secondary' : 'primary'}
                        onClick={() => toggleAdminStatus(admin)}
                        style={{padding: '0.5rem 1rem', fontSize: '0.8rem' }}
                      >
                        {admin.isActive ? 'Deactivate' : 'Activate'}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </tbody>
            </AdminTable>
          )}
        </ListSection>
      </ContentWrapper>
    </Container>
  );
};

export default AdminRegistration;